
import { useState, useEffect } from 'react';
import { Users, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import type { AnalysisResult } from '@/types/analysis';

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

interface CategoryScore {
  category: string;
  score: number;
  count: number;
}

const categories = [
  "Rapport Building & Trust",
  "Tactical Empathy",
  "Labeling & Mirroring",
  "Calibrated Questions",
  "No Oriented Questions",
  "Control of Negotiation",
  "Overall Effectiveness"
];

const TeamAnalysis = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [categoryScores, setCategoryScores] = useState<Record<string, number>>({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileAndAnalyses = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/');
          return;
        }

        // Fetch profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) throw profileError;
        setProfile(profileData);

        // Fetch call analyses
        const { data: analyses, error: analysesError } = await supabase
          .from('call_analyses')
          .select('*')
          .eq('user_id', session.user.id);

        if (analysesError) throw analysesError;

        // Calculate average scores for each category
        const categoryTotals: Record<string, CategoryScore> = {};

        analyses.forEach(analysis => {
          const feedbackData = analysis.feedback as unknown as AnalysisResult['feedback'];
          
          if (feedbackData?.feedback) {
            feedbackData.feedback.forEach(categoryFeedback => {
              const categoryName = categoryFeedback.category === "Implementation of \"No\"-Oriented Questions" 
                ? "No Oriented Questions" 
                : categoryFeedback.category;

              if (!categoryTotals[categoryName]) {
                categoryTotals[categoryName] = {
                  category: categoryName,
                  score: 0,
                  count: 0
                };
              }
              
              categoryTotals[categoryName].score += categoryFeedback.score;
              categoryTotals[categoryName].count += 1;
            });
          }
        });

        // Calculate averages and store in state
        const averageScores: Record<string, number> = {};
        Object.values(categoryTotals).forEach(({ category, score, count }) => {
          averageScores[category] = count > 0 ? Number((score / count).toFixed(1)) : 0;
        });

        // Add overall effectiveness from the last analysis if available
        if (analyses.length > 0) {
          const latestAnalysis = analyses[analyses.length - 1];
          const feedbackData = latestAnalysis.feedback as unknown as AnalysisResult['feedback'];
          averageScores["Overall Effectiveness"] = feedbackData?.summary?.overallScore || 0;
        }

        setCategoryScores(averageScores);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileAndAnalyses();
  }, [navigate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Link to="/dashboard">
          <Button variant="ghost" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="h-6 w-6" />
          Team Performance Analysis
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {profile && (
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">
              {profile.first_name && profile.last_name 
                ? `${profile.first_name} ${profile.last_name}`
                : 'Team Member'}
            </h3>
            <div className="space-y-3">
              {categories.map((category, index) => (
                <div key={index} className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">{category}</span>
                  <span className="text-gray-900 font-medium">
                    {categoryScores[category] ? `${categoryScores[category]}/10` : '--'}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TeamAnalysis;
